import React, { useState } from 'react';

import { cn, ia } from 'lib/helpers/utility';
import useTimezone from 'lib/hooks/useTimezone';

import Icon from '../../Icon/Icon';
import Input from '../../Input/Input';
import DatePicker from '../../DatePicker/DatePicker';
import Typography from '../../Typography/Typography';
import { getOptionsByDirection, getSuggestions } from '../lib/helpers';

const Option = ({ label, optionId, onClick, selectedOption }) => (
  <button
    type="button"
    className="flex h-8 w-full items-center justify-between px-4 hover:bg-primary-50"
    onClick={onClick}>
    <Typography className="cursor-pointer whitespace-nowrap" size="small">
      {label}
    </Typography>
    {selectedOption?.id === optionId && <Icon icon="check-linear" color="neutral" shadow={700} />}
  </button>
);

const DateRangeContent = ({
  onChange,
  value,
  onClose = () => {},
  className,
  dataKey,
  formula,
  data,
  direction: directionProp
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [input, setInput] = useState('');
  const timezone = useTimezone();
  const direction = directionProp || data?.direction || 'past';

  const handleChange = (event) => {
    const val = event.target.value;
    setInput(val);

    const number = parseInt(val, 10);
    if (!isNaN(number) && number >= 0) {
      setSuggestions(getSuggestions(direction, number, timezone));
    } else {
      setSuggestions([]);
    }
  };

  const onSelectOption = (option) => {
    const optionId = option.id || option.label;
    setInput('');
    setSuggestions([]);
    if (!option.value) {
      onChange(
        { id: optionId, startDate: null, endDate: null, timezone, label: option.label },
        dataKey
      );
      onClose();
      return;
    }
    onChange({ id: optionId, ...option.value, label: option.label, timezone }, dataKey, formula);
    onClose();
  };

  const onRangeChange = (ranges) => {
    if (!ranges?.[0] || !ranges?.[1]) return;
    const [startDate, endDate] = ranges;
    onChange({ id: 'custom-date-range', startDate, endDate, timezone }, dataKey, formula);
    onClose();
  };

  const defaultOptions = getOptionsByDirection(direction, timezone);
  const toRenderOptions = ia(suggestions)
    ? suggestions
    : defaultOptions.filter((o) => o.id !== 'custom-date-range');

  return (
    <div className={cn('flex flex-col gap-y-2 pb-2', className)}>
      <Input
        inputWrapperClassName="w-full border-0 border-b border-neutral-200 rounded-none"
        placeholder="Custom range: 2d, 4d, 8weeks..."
        value={input}
        onChange={handleChange}
      />
      {toRenderOptions.map((option) => (
        <Option
          key={option.id || option.label}
          label={option.label}
          optionId={option.id || option.label}
          onClick={() => onSelectOption(option)}
          selectedOption={value}
        />
      ))}
      <DatePicker
        isDateRange
        isTimeRange
        isTimezone
        showTimeRightComponent={false}
        value={value?.startDate && value?.endDate ? [value.startDate, value.endDate] : null}
        onChange={onRangeChange}
        customButtonTrigger={
          <div className="flex h-8 cursor-pointer items-center justify-between px-4 hover:bg-primary-50">
            <Typography className="cursor-pointer whitespace-nowrap" size="small">
              Custom range
            </Typography>
          </div>
        }
      />
    </div>
  );
};

export default DateRangeContent;
